import React, { useState, useEffect } from "react";
import {
  IonPage,
  IonContent,
  IonToolbar,
  IonLabel,
  IonItem,
  IonInput,
  IonList,
  IonButton,
  IonFooter,
} from "@ionic/react";
import { useHistory } from "react-router-dom";
import { useOnboarding } from "@/hooks/use-onboarding";
import { z } from "zod";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import CountrySelect from "../CountrySelect";

const ProfileSetup: React.FC<{ nextStep: string; }> = ({ nextStep }) => {
  const history = useHistory();
  const { updateOnboardingData, onboardingData } = useOnboarding();
  const { t } = useTranslation();

  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    countryCode: "FR",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  useEffect(() => {
    if (onboardingData.firstName) {
      setFormData(onboardingData);
    }
  }, [onboardingData]);

  const formSchema = z.object({
    firstName: z.string().min(1, t("first_name_required")).max(50),
    lastName: z.string().min(1, t("last_name_required")).max(50),
    countryCode: z.string(),
  });

  const handleInputChange = (name: string, value: string) => {
    setFormData({ ...formData, [name]: value });
    setErrors({ ...errors, [name]: "" });
  };

  const handleSubmit = async () => {
    try {
      formSchema.parse(formData);
      await updateOnboardingData(formData);
      history.push(nextStep);
    } catch (error) {
      if (error instanceof z.ZodError) {
        const newErrors: { [key: string]: string } = {};
        error.errors.forEach((err) => {
          if (err.path) {
            newErrors[err.path[0]] = err.message;
          }
        });
        setErrors(newErrors);
      }
    }
  };

  const isFormValid = () => {
    return formData.firstName.trim() !== '' && formData.lastName.trim() !== '' && formData.countryCode !== '';
  };

  return (
    <IonPage>
      <IonContent>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col md:items-center text-start md:justify-center h-full bg-card"
        >
          <IonList className="space-y-6 min-w-96 p-4" lines="none">
            <h1 className="text-3xl font-semibold text-start">{t("profile")}</h1>
            <IonItem className="custom-item">
              <IonInput
                labelPlacement="floating"
                label={t('first_name')}
                value={formData.firstName}
                onIonChange={(e) =>
                  handleInputChange("firstName", e.detail.value!)
                }
                placeholder="Tiger"
              />
            </IonItem>
            {errors.firstName && (
              <IonLabel color="danger" className="mt-1">
                {errors.firstName}
              </IonLabel>
            )}

            <IonItem className="custom-item">
              <IonInput
                labelPlacement="floating"
                label={t("last_name")}
                value={formData.lastName}
                onIonChange={(e) =>
                  handleInputChange("lastName", e.detail.value!)
                }
                placeholder="Woods"
              />
            </IonItem>
            {errors.lastName && (
              <IonLabel color="danger" className="mt-1">
                {errors.lastName}
              </IonLabel>
            )}

            <CountrySelect className="bg-background" defaultValue={formData.countryCode} placeholder={t("country")} onChange={(code) => handleInputChange("countryCode", code)} />
            <IonButton
              shape="round"
              expand="full"
              className="mt-8 hidden md:block"
              onClick={handleSubmit}
              disabled={!isFormValid()}
            >
              {t("next")}
            </IonButton>
          </IonList>
        </motion.div>
      </IonContent>
      <IonFooter className="ion-no-border bg-card md:hidden">
        <IonToolbar>
          <IonButton
            shape="round"
            expand="block"
            className="m-4"
            onClick={handleSubmit}
            disabled={!isFormValid()}
          >
            {t("next")}
          </IonButton>
        </IonToolbar>
      </IonFooter>
    </IonPage>
  );
};

export default ProfileSetup;